import { Text, ScrollView, Checkbox, XStack, Button, Label, Popover, YStack, useMedia, Card, H3, View } from "tamagui";
import { XPDataTable } from "../../../UtilComponents/XPDataTable.mjs";
import { PriorityBadge } from "../../../custom-ui/misc.mjs";
import { color as colorConfig, MiscUtils, TZABrandGuidelines } from "@my/util";
import { Check as CheckIcon, Edit3, Trash } from "@tamagui/lucide-icons-2";
import { SimpleText } from "../texts/index.mjs";
import { createColumnHelper, useReactTable, getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
const CheckboxWithLabel = ({
  size,
  label,
  gap,
  backgroundColor,
  ...checkboxProps
}) => {
  const id = `checkbox-${(size || "").toString().slice(1)}`;
  return /* @__PURE__ */jsxs(XStack, {
    gap,
    alignItems: "center",
    style: {
      cursor: "pointer"
    },
    children: [/* @__PURE__ */jsx(Checkbox, {
      size,
      ...checkboxProps,
      activeStyle: {
        backgroundColor
      },
      children: /* @__PURE__ */jsx(Checkbox.Indicator, {
        children: /* @__PURE__ */jsx(CheckIcon, {
          color: "#fff"
        })
      })
    }), label ?? /* @__PURE__ */jsx(Label, {
      size,
      htmlFor: id,
      children: label
    })]
  });
};
const RenderCellContent = ({
  value,
  color,
  row
}) => {
  const valuesArray = Array.isArray(value) ? value : [value];
  return /* @__PURE__ */jsxs(Fragment, {
    children: [...valuesArray.map((val, valIndex) => {
      const uniqueKey = `${valIndex}-${JSON.stringify(val)}`;
      if (typeof val === "boolean") {
        return /* @__PURE__ */jsx(YStack, {
          children: /* @__PURE__ */jsx(CheckboxWithLabel, {
            checked: val,
            size: "$5",
            gap: "$4",
            backgroundColor: "#D9D9D9",
            borderRadius: 5
          })
        }, uniqueKey);
      }
      if (typeof val === "object") {
        if (val.checkbox) {
          return /* @__PURE__ */jsx(YStack, {
            children: /* @__PURE__ */jsx(CheckboxWithLabel, {
              checked: val.checkbox.value,
              onCheckedChange: checked => val.checkbox.onChange(!!checked),
              size: "$5",
              gap: "$4",
              backgroundColor: val.checkbox.value ? TZABrandGuidelines.colorsMap.primaryGreen : "#D9D9D9",
              borderRadius: 5
            })
          }, uniqueKey);
        }
        if (val.edit || val.delete) {
          return /* @__PURE__ */jsxs(XStack, {
            width: "50%",
            minWidth: 45,
            justifyContent: "space-between",
            children: [val.edit ? /* @__PURE__ */jsx(Button, {
              unstyled: true,
              onPress: val.edit,
              pressStyle: {
                opacity: 0.5
              },
              hoverStyle: {
                opacity: 0.5
              },
              icon: /* @__PURE__ */jsx(Edit3, {
                size: "$1",
                color: "green"
              })
            }) : null, val.delete ? /* @__PURE__ */jsx(Button, {
              unstyled: true,
              onPress: val.delete,
              pressStyle: {
                opacity: 0.5
              },
              hoverStyle: {
                opacity: 0.5
              },
              icon: /* @__PURE__ */jsx(Trash, {
                size: "$1",
                color: "red"
              })
            }) : null]
          }, uniqueKey);
        }
        if (val.status) {
          const [backgroundColor, textColor] = val?.status === "Completed" ? ["rgba(133, 193, 157, 0.21)", "#85C19D"] : ["#ffffcc", "#a5642a"];
          return /* @__PURE__ */jsx(YStack, {
            children: /* @__PURE__ */jsx(YStack, {
              bg: backgroundColor,
              height: 30,
              width: 100,
              borderRadius: 100,
              alignItems: "center",
              justifyContent: "center",
              children: /* @__PURE__ */jsx(SimpleText, {
                fontSize: 12,
                fontWeight: "700",
                col: textColor,
                children: val?.status
              })
            })
          }, uniqueKey);
        }
        if ("isGain" in val) {
          const colorScheme = val.value === "-" ? colorConfig.darkNavy : val.isGain ? "#15B100" : "#FF6B6B";
          return /* @__PURE__ */jsxs(XStack, {
            alignItems: "center",
            gap: 5,
            children: [val.value !== "-" ? /* @__PURE__ */jsx(Text, {
              fontSize: 18,
              fontWeight: "700",
              rotate: val.isGain ? "45deg" : "-45deg",
              color: colorScheme,
              children: val.isGain ? `\u2191` : `\u2193`
            }) : null, /* @__PURE__ */jsx(Text, {
              paddingVertical: 15,
              fontSize: 16,
              fontWeight: "700",
              color: colorScheme,
              children: val.value || ""
            })]
          }, uniqueKey);
        }
        if (val.priorityBadge) {
          return /* @__PURE__ */jsx(PriorityBadge, {
            omitPriorityText: true,
            priority: val.priority
          });
        }
        if (val.expander) {
          return row?.getCanExpand() ? val.expander.renderFn({
            onClick: row.getToggleExpandedHandler(),
            isExpanded: row.getIsExpanded()
          }) : /* @__PURE__ */jsx(XStack, {});
        }
        return /* @__PURE__ */jsx(XStack, {
          alignItems: "center",
          children: /* @__PURE__ */jsx(Text, {
            fontSize: 16,
            fontWeight: "700",
            color: color ? color : colorConfig.darkNavy,
            children: JSON.stringify(val)
          })
        }, uniqueKey);
      }
      return /* @__PURE__ */jsx(Text, {
        paddingVertical: 15,
        fontSize: 16,
        fontWeight: "700",
        color: color ? color : colorConfig.darkNavy,
        textAlign: "center",
        children: val || ""
      }, uniqueKey);
    })]
  });
};
const MobileRenderCellContent = ({
  value,
  color,
  row
}) => {
  const valuesArray = Array.isArray(value) ? value : [value];
  return /* @__PURE__ */jsxs(Fragment, {
    children: [...valuesArray.map((val, valIndex) => {
      const uniqueKey = `${valIndex}-${JSON.stringify(val)}`;
      let output;
      if (typeof val === "boolean") {
        output = /* @__PURE__ */jsx(CheckboxWithLabel, {
          checked: val,
          size: "$5",
          gap: "$4",
          backgroundColor: "#D9D9D9",
          borderRadius: 5
        });
      } else if (typeof val === "object") {
        if (val.checkbox) {
          output = /* @__PURE__ */jsxs(XStack, {
            style: {
              gap: 5,
              alignItems: "center"
            },
            children: [/* @__PURE__ */jsx(Text, {
              style: {
                fontSize: 10
              },
              children: "Mark as Completed:"
            }), /* @__PURE__ */jsx(CheckboxWithLabel, {
              checked: val.checkbox.value,
              onCheckedChange: checked => val.checkbox.onChange(!!checked),
              size: "$5",
              gap: "$4",
              backgroundColor: val.checkbox.value ? TZABrandGuidelines.colorsMap.primaryGreen : "#D9D9D9",
              borderRadius: 5
            })]
          });
        } else if (val.edit || val.delete) {
          output = /* @__PURE__ */jsxs(XStack, {
            width: "50%",
            minWidth: 45,
            justifyContent: "space-between",
            children: [val.edit ? /* @__PURE__ */jsx(Button, {
              unstyled: true,
              onPress: val.edit,
              pressStyle: {
                opacity: 0.5
              },
              hoverStyle: {
                opacity: 0.5
              },
              icon: /* @__PURE__ */jsx(Edit3, {
                size: "$1",
                color: "green"
              })
            }) : null, val.delete ? /* @__PURE__ */jsx(Button, {
              unstyled: true,
              onPress: val.delete,
              pressStyle: {
                opacity: 0.5
              },
              hoverStyle: {
                opacity: 0.5
              },
              icon: /* @__PURE__ */jsx(Trash, {
                size: "$1",
                color: "red"
              })
            }) : null]
          });
        } else if (val.status) {
          const [backgroundColor, textColor] = val?.status === "Completed" ? ["rgba(133, 193, 157, 0.21)", "#85C19D"] : ["#ffffcc", "#a5642a"];
          output = /* @__PURE__ */jsx(YStack, {
            bg: backgroundColor,
            height: 30,
            width: 100,
            borderRadius: 100,
            alignItems: "center",
            justifyContent: "center",
            children: /* @__PURE__ */jsx(SimpleText, {
              fontSize: 12,
              fontWeight: "700",
              color: textColor,
              children: val?.status
            })
          });
        } else if ("isGain" in val) {
          const colorScheme = val.value === "-" ? "#6C757D" : val.isGain ? "#15B100" : "#FF6B6B";
          output = /* @__PURE__ */jsxs(XStack, {
            alignItems: "center",
            gap: 5,
            children: [val.value !== "-" ? /* @__PURE__ */jsx(Text, {
              fontSize: 18,
              fontWeight: "700",
              rotate: val.isGain ? "45deg" : "-45deg",
              color: colorScheme,
              children: val.isGain ? `\u2191` : `\u2193`
            }) : null, /* @__PURE__ */jsx(Text, {
              paddingVertical: 15,
              "$max-sm": {
                paddingVertical: 0
              },
              fontSize: 16,
              fontWeight: "700",
              color: colorScheme,
              children: val.value || ""
            })]
          });
        } else if (val.priorityBadge) {
          output = /* @__PURE__ */jsx(PriorityBadge, {
            slim: true,
            omitPriorityText: true,
            priority: val.priority,
            containerProps: {
              marginLeft: 0
            }
          });
        } else if (val.expander) {
          output = row?.getCanExpand() ? val.expander.renderFn({
            onClick: row.getToggleExpandedHandler(),
            isExpanded: row.getIsExpanded()
          }) : /* @__PURE__ */jsx(XStack, {});
        } else {
          output = /* @__PURE__ */jsx(XStack, {
            alignItems: "center",
            children: /* @__PURE__ */jsx(Text, {
              fontSize: 16,
              fontWeight: "700",
              color: color ? color : "#6C757D"
            })
          }, uniqueKey);
        }
      }
      return output === void 0 ? /* @__PURE__ */jsx(Text, {
        fontSize: 16,
        color: color ? color : colorConfig.darkNavy,
        children: val || ""
      }, uniqueKey) : output;
    })]
  });
};
const InfoPopup = infoText => /* @__PURE__ */jsxs(Popover, {
  size: "$5",
  children: [/* @__PURE__ */jsx(Popover.Trigger, {
    asChild: true,
    children: /* @__PURE__ */jsx(Button, {
      unstyled: true,
      pressStyle: {
        opacity: 0.5
      },
      borderWidth: 1,
      height: 20,
      width: 20,
      circular: true,
      alignItems: "center",
      justifyContent: "center",
      borderColor: "rgb(23, 23, 23)",
      bg: "transparent",
      children: /* @__PURE__ */jsx(Text, {
        unstyled: true,
        fontSize: 9,
        fontWeight: 700,
        color: "rgb(23, 23, 23)",
        children: "i"
      })
    })
  }), /* @__PURE__ */jsxs(Popover.Content, {
    borderWidth: 1,
    borderRadius: "$2",
    padding: "$3",
    backgroundColor: "#495057",
    enterStyle: {
      opacity: 0,
      scale: 0.9
    },
    exitStyle: {
      opacity: 0,
      scale: 0.9
    },
    transition: "quick",
    width: 230,
    children: [/* @__PURE__ */jsx(Text, {
      color: "#fff",
      fontSize: 14,
      children: infoText ? infoText : "Lorem ipsum dolor sit amet, consectetur adipiscing elit."
    }), /* @__PURE__ */jsx(Popover.Arrow, {
      borderWidth: 1,
      boc: "#495057",
      backgroundColor: "#495057"
    })]
  })]
});
const TableHeaderCell = ({
  item
}) => /* @__PURE__ */jsxs(Fragment, {
  children: [/* @__PURE__ */jsx(Text, {
    color: "rgb(23, 23, 23)",
    fontWeight: "300",
    fontSize: item.text === "Success Probability" ? 13 : 15,
    children: item.text
  }), item?.info && InfoPopup(item.infoText)]
});
const columnHelper = createColumnHelper();
const FinancialTable = function (props) {
  const {
    data,
    headings,
    backgroundColor,
    items = [],
    columnWidth,
    expandProps,
    noDataMessage,
    tableContainerStyle
  } = props;
  const media = useMedia();
  if (media["max-md"] && props.mobileRenderOptions && !props.skipMobileList) {
    return /* @__PURE__ */jsx(FinancialTableMobileList, {
      ...props
    });
  } else {
    return /* @__PURE__ */jsx(FinancialTableDesktop, {
      ...props
    });
  }
};
const FinancialTableDesktop = function (props) {
  const {
    data,
    headings,
    backgroundColor,
    items = [],
    columnWidth,
    expandProps,
    noDataMessage,
    tableContainerStyle
  } = props;
  const typedColumnHelper = columnHelper;
  const columns = useMemo(() => [...headings.map(heading => typedColumnHelper.accessor(heading.key, {
    id: heading.key,
    header: () => /* @__PURE__ */jsx(XStack, {
      flex: 1,
      alignItems: "center",
      height: 44,
      borderRightColor: "#63995D",
      width: columnWidth ?? 150,
      flexDirection: "row",
      justifyContent: "center",
      gap: 5,
      children: /* @__PURE__ */jsx(TableHeaderCell, {
        item: heading
      })
    }, heading.key),
    cell: info => /* @__PURE__ */jsx(XStack, {
      flex: 1,
      justifyContent: "center",
      width: columnWidth ?? 150,
      paddingVertical: 5,
      paddingHorizontal: 10,
      alignItems: "center",
      borderRightColor: "#63995D",
      backgroundColor,
      textAlign: "center",
      children: /* @__PURE__ */jsx(RenderCellContent, {
        value: info.getValue(),
        row: info.row
      })
    }, heading.key)
  }))], []);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(expandProps ? {
      getRowCanExpand: expandProps.getRowCanExpand,
      getExpandedRowModel: getExpandedRowModel()
    } : {})
  });
  if (data.length === 0) {
    return /* @__PURE__ */jsx(Text, {
      children: noDataMessage ? noDataMessage : "No data available"
    });
  }
  const newTable = /* @__PURE__ */jsx(XPDataTable, {
    table,
    tableContainerProps: {
      backgroundColor: "white",
      flexDirection: "column",
      //flex: 1,
      minWidth: 320,
      "$platform-web": {
        flexGrow: 1
      },
      style: {
        ...tableContainerStyle
      }
    },
    headerGroupProps: {
      borderRadius: 4,
      backgroundColor: "rgb(246, 246, 246)",
      width: "100%",
      gap: 10,
      style: {}
      //{borderBottom: '2px solid '+ colorConfig.tzaGreen}
    },
    bodyRowProps: {
      gap: 10,
      alignItems: "stretch",
      width: "100%",
      borderBottomColor: "#E0E0E0",
      borderBottomWidth: 1
    },
    ...(expandProps || {})
  });
  return /* @__PURE__ */jsx(ScrollView, {
    horizontal: true,
    style: {
      minWidth: 320,
      width: "100%"
    },
    contentContainerStyle: {
      minWidth: 320,
      justifyContent: "center",
      flexGrow: 1
    },
    children: /* @__PURE__ */jsxs(YStack, {
      style: {
        borderWidth: 1,
        borderColor: "rgb(240, 240, 240)",
        borderRadius: 5,
        flexGrow: 1
      },
      children: [newTable, items.length > 0 && /* @__PURE__ */jsx(XStack, {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        children: items.map((item, index) => /* @__PURE__ */jsx(XStack, {
          flex: 1,
          justifyContent: "center",
          width: columnWidth ?? 150,
          padding: 10,
          alignItems: "center",
          children: /* @__PURE__ */jsx(RenderCellContent, {
            value: item.text.toUpperCase(),
            color: item.color
          })
        }, index))
      })]
    })
  });
};
const mobileItemRender = (headerObj, row, options) => {
  return /* @__PURE__ */jsxs(Fragment, {
    children: [options?.omitHeader && headerObj.text ? null : /* @__PURE__ */jsxs(XStack, {
      style: {
        width: "100%",
        backgroundColor: "rgba(235,235,235, 0)",
        alignItems: "center",
        gap: 5,
        height: 25
      },
      children: [/* @__PURE__ */jsx(Text, {
        style: {
          fontSize: 10,
          fontWeight: 700
        },
        children: headerObj.text
      }), headerObj.info ? InfoPopup(headerObj.infoText) : null]
    }), /* @__PURE__ */jsx(XStack, {
      children: /* @__PURE__ */jsx(MobileRenderCellContent, {
        value: row.original[headerObj.key],
        row
      })
    })]
  });
};
const FinancialTableMobileList = function (props) {
  const {
    data,
    headings,
    backgroundColor,
    items = [],
    columnWidth,
    expandProps,
    noDataMessage,
    tableContainerStyle,
    mobileRenderOptions
  } = props;
  const headerMap = Object.assign({}, ...headings.map(heading => ({
    [heading.key]: heading
  })));
  const titleObj = headerMap[mobileRenderOptions.titleKey];
  const [expandedState, setExpandedState] = useState({});
  const toggleRowExpanded = idx => () => setExpandedState(currState => {
    currState[idx] = !currState[idx];
    return {
      ...currState
    };
  });
  return /* @__PURE__ */jsx(YStack, {
    gap: 10,
    style: {
      width: "100%"
    },
    children: data.map((row, idx) => {
      const rowObj = {
        original: row
      };
      if (expandProps) {
        rowObj["getCanExpand"] = expandProps.getRowCanExpand;
        rowObj["getToggleExpandedHandler"] = () => toggleRowExpanded(idx);
        rowObj["getIsExpanded"] = () => expandedState[idx];
      }
      return /* @__PURE__ */jsx(Card, {
        style: {
          backgroundColor: "white",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: "rgba(108, 117, 125, 0.24)",
          borderLeftWidth: 4,
          borderLeftColor: colorConfig.tzaGreen,
          padding: 10
        },
        elevation: 1,
        ...MiscUtils.ui.shadowGenerator(0.13, 5),
        children: /* @__PURE__ */jsxs(YStack, {
          style: {
            width: "100%"
          },
          children: [/* @__PURE__ */jsx(XStack, {
            style: {
              width: "100%",
              marginBottom: 5,
              paddingBottom: 10,
              borderBottomWidth: 1,
              borderBottomColor: "rgba(0,0,0,.1)"
            },
            children: /* @__PURE__ */jsxs(YStack, {
              style: {
                width: "100%"
              },
              children: [/* @__PURE__ */jsxs(XStack, {
                style: {
                  width: "100%",
                  flex: 1
                },
                children: [/* @__PURE__ */jsx(Text, {
                  children: titleObj?.text
                }), titleObj?.info ? InfoPopup(titleObj.infoText) : null, /* @__PURE__ */jsx(View, {
                  style: {
                    minWidth: 5,
                    flex: 1
                  }
                }), /* @__PURE__ */jsx(YStack, {
                  style: {
                    flexShrink: 1
                  },
                  children: mobileRenderOptions.interactiveKeys?.map((valueKey, idx2) => {
                    return /* @__PURE__ */jsx(YStack, {
                      style: {
                        flexShrink: 1,
                        width: 45
                      },
                      children: mobileItemRender(headerMap[valueKey], rowObj, {
                        omitHeader: true
                      })
                    }, idx2);
                  })
                })]
              }), titleObj ? /* @__PURE__ */jsx(XStack, {
                children: /* @__PURE__ */jsx(H3, {
                  style: {
                    fontSize: 20,
                    lineHeight: 20,
                    color: colorConfig.darkNavy,
                    ...(mobileRenderOptions?.titleTextStyle || {})
                  },
                  children: row[titleObj.key]
                })
              }) : null]
            })
          }), /* @__PURE__ */jsx(XStack, {
            style: {
              rowGap: 5,
              flexWrap: "wrap"
            },
            children: mobileRenderOptions.mainValueKeys?.map((valueKey, idx2) => {
              return /* @__PURE__ */jsx(YStack, {
                minWidth: "50%",
                style: {
                  flex: 1
                },
                children: mobileItemRender(headerMap[valueKey], rowObj)
              }, idx2);
            })
          }), /* @__PURE__ */jsx(XStack, {
            style: {
              rowGap: 5,
              flexWrap: "wrap"
            },
            children: mobileRenderOptions.otherKeys?.map((keyRow, idx2) => {
              return /* @__PURE__ */jsx(YStack, {
                minWidth: "50%",
                children: keyRow.map((valueKey, idx22) => {
                  return /* @__PURE__ */jsx(YStack, {
                    style: {
                      flex: 1
                    },
                    children: mobileItemRender(headerMap[valueKey], rowObj)
                  }, idx22);
                })
              }, idx2);
            })
          }), expandProps?.forceExpanded || expandedState[idx] ? expandProps.renderSubComponent(rowObj, idx) : null]
        })
      }, idx);
    })
  });
};
export { CheckboxWithLabel, FinancialTable };
//# sourceMappingURL=FinancialTable.mjs.map
